import React, { createContext, useContext } from 'react';
import { useReducedMotion } from 'framer-motion';

/* ─────────────────────────────────────────────────────────────
   The animation gate.

   Every page is rendered to static HTML at build time (see
   prerender.js) so that crawlers which do not execute JavaScript
   (which is most AI crawlers) get the real page instead of an empty
   <div id="root">. That only helps if the static HTML is *readable*,
   and the reveal animations are directly at odds with it:

     - WordReveal splits a headline into one <span> per word with the
       gap set as a margin, so there is no whitespace text node between
       them. Extracting the text of that <h1> yields
       "Yourvision,builtwithprecision."
     - Reveal and friends start at `opacity: 0`, which lands in the
       static markup as inline style. Text nobody can see is text a
       search engine is entitled to distrust, and a visitor whose
       JavaScript is slow or blocked sees a blank page.

   So the gate is shut for the prerender, and only for the prerender.
   It renders the settled, plain, fully legible form of every page.
   In a browser it is open from the very first render, which means the
   animation behaviour is byte for byte what it was before any of this
   existed: no flip, no second render, nothing to get out of step.
   ───────────────────────────────────────────────────────────── */

const AnimationContext = createContext(true);

export function AnimationProvider({ children }) {
    /* The prerender sets window.__PRERENDER__ before the bundle runs.
       Nothing else ever does, so this is a constant for the life of the
       page in both environments. */
    const enabled = typeof window === 'undefined' || !window.__PRERENDER__;

    return (
        <AnimationContext.Provider value={enabled}>
            {children}
        </AnimationContext.Provider>
    );
}

/**
 * True when this render may animate: the gate is open and the reader has
 * not asked for reduced motion. Everything that used to branch on
 * `useReducedMotion()` alone branches on this instead, so the settled
 * markup is shared by the prerender and by the reduced-motion reader.
 */
export default function useAnimate() {
    const reduced = useReducedMotion();
    const enabled = useContext(AnimationContext);

    return enabled && !reduced;
}

/**
 * Props that make a mount animation respect the gate.
 *
 * A `motion` component with a plain `initial` writes its starting styles
 * into the markup, and by the time the prerender captures the page those
 * have animated to their finished values, so the static HTML would hold
 * whatever the animation happened to be doing. With the gate shut this
 * renders `initial={false}`: framer paints the finished state and animates
 * nothing, which is exactly what a crawler should be given.
 */
export function entry(animate, from) {
    return animate
        ? { key: 'live', initial: from }
        : { key: 'static', initial: false };
}
